import { FC, ReactNode } from 'react';
import { Collapse } from 'antd';
import {
    TbActivityHeartbeat,
    TbChevronDown,
    TbFileInfo,
    TbKeyframes,
    TbLanguage,
    TbMoodSearch,
    TbTags,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SearchFilterSectionLabel from '@/shared/components/filters/SearchFilterSectionLabel';
import SearchFilterSectionOverflow from '@/shared/components/filters/SearchFilterSectionOverflow';

import { DocumentKeywords, Language, Metadata, Sentiment, Sources, Tags } from './options';

interface FiltersProps {
    filters: unknown;
    handleChange: (path: string[], value: unknown) => void;
    presentation?: 'collapse' | 'editor';
    selectedSectionKey?: string;
}

interface DocumentFilterSection {
    children: ReactNode;
    icon: ReactNode;
    key: string;
    label: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const getDocumentFilterSections = (
    filters: unknown,
    handleChange: (path: string[], value: unknown) => void,
): DocumentFilterSection[] => {
    const values = isRecord(filters) ? filters : {};
    const sectionProps = { filters: values, handleChange };

    return [
        {
            children: <Metadata {...sectionProps} />,
            icon: <TbFileInfo />,
            key: 'metadata',
            label: 'Metadata',
        },
        {
            children: <Language {...sectionProps} />,
            icon: <TbLanguage />,
            key: 'language',
            label: 'Language',
        },
        {
            children: <Sources {...sectionProps} />,
            icon: <TbActivityHeartbeat />,
            key: 'sources',
            label: 'Sources',
        },
        {
            children: <DocumentKeywords {...sectionProps} />,
            icon: <TbKeyframes />,
            key: 'keywords',
            label: 'Keywords',
        },
        {
            children: <Tags {...sectionProps} />,
            icon: <TbTags />,
            key: 'tags',
            label: 'Tags',
        },
        {
            children: <Sentiment {...sectionProps} />,
            icon: <TbMoodSearch />,
            key: 'sentiment',
            label: 'Sentiment',
        },
    ];
};

const Filters: FC<FiltersProps> = ({
    filters,
    handleChange,
    presentation = 'collapse',
    selectedSectionKey,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const sections = getDocumentFilterSections(filters, handleChange);

    if (presentation === 'editor') {
        return <>{sections.find((section) => section.key === selectedSectionKey)?.children}</>;
    }

    return (
        <>
            <small style={{ color: token.colorTextTertiary }}>Document Filters ({sections.length})</small>
            <Collapse
                expandIcon={({ isActive }) => (
                    <TbChevronDown
                        size='1rem'
                        style={{
                            color: token.colorTextTertiary,
                            transform: `rotate(${isActive ? '0deg' : '-90deg'})`,
                        }}
                    />
                )}
                expandIconPosition='end'
                items={sections.map((section) => ({
                    children: <SearchFilterSectionOverflow>{section.children}</SearchFilterSectionOverflow>,
                    key: section.key,
                    label: <SearchFilterSectionLabel icon={section.icon} label={section.label} />,
                }))}
                size='small'
                style={{ backgroundColor: token.colorBgBase, borderColor: token.colorBorder, margin: '0.75rem 0' }}
            />
        </>
    );
};

export default Filters;
